import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import {
    Key,
    ExternalLink,
    Check,
    X,
    Eye,
    EyeOff,
    Loader2,
    Copy,
    Trash2,
    Info,
    PlayCircle,
    CheckCircle,
    AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function OAuthSettings() {
    const { api } = useAuth();
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editPlatform, setEditPlatform] = useState(null);
    const [showSecret, setShowSecret] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [testing, setTesting] = useState(null);
    const [testResult, setTestResult] = useState(null);
    
    const [formData, setFormData] = useState({
        client_id: '',
        client_secret: ''
    });

    useEffect(() => {
        fetchPlatforms();
    }, []);

    const fetchPlatforms = async () => {
        try {
            setLoading(true);
            const response = await api.get('/oauth-settings/platforms');
            setPlatforms(response.data);
        } catch (error) {
            console.error('Failed to fetch platforms:', error);
            if (error.response?.status === 403) {
                toast.error('Admin access required to manage OAuth settings');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (platform) => {
        try {
            const response = await api.get(`/oauth-settings/platforms/${platform.platform}`);
            setEditPlatform(response.data);
            setFormData({ client_id: '', client_secret: '' });
            setShowSecret(false);
        } catch (error) {
            toast.error('Failed to load platform details');
        }
    };

    const handleSave = async () => {
        if (!formData.client_id || !formData.client_secret) {
            toast.error('Please fill in both Client ID and Client Secret');
            return;
        }

        try {
            setSaving(true);
            await api.post(`/oauth-settings/platforms/${editPlatform.platform}`, {
                platform: editPlatform.platform,
                client_id: formData.client_id,
                client_secret: formData.client_secret
            });
            toast.success(`${editPlatform.name} credentials saved successfully`);
            setEditPlatform(null);
            fetchPlatforms();
        } catch (error) {
            toast.error('Failed to save credentials');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async (platformId) => {
        try {
            setTesting(platformId);
            setTestResult(null);
            const response = await api.post(`/oauth-settings/platforms/${platformId}/test`);
            setTestResult(response.data);
            
            if (response.data.success) {
                toast.success(response.data.message);
            } else if (response.data.error_type === 'validation_warning') {
                toast.warning(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to test connection');
            setTestResult({
                success: false,
                message: 'Failed to test connection - server error'
            });
        } finally {
            setTesting(null);
        }
    };

    const handleDelete = async (platform) => {
        try {
            await api.delete(`/oauth-settings/platforms/${platform}`);
            toast.success('Credentials deleted');
            setDeleteDialog(null);
            fetchPlatforms();
        } catch (error) {
            toast.error('Failed to delete credentials');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">OAuth Configuration</h2>
                    <p className="text-muted-foreground">
                        Configure API credentials for each social platform
                    </p>
                </div>
                <Badge variant="outline" className="flex items-center gap-1">
                    <Key className="h-3 w-3" />
                    Admin Only
                </Badge>
            </div>

            {/* Info Card */}
            <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-primary mt-0.5" />
                        <div className="space-y-1">
                            <p className="font-medium">How it works</p>
                            <p className="text-sm text-muted-foreground">
                                Add your OAuth credentials from each platform's developer portal. 
                                These credentials are encrypted and stored securely. Once configured, 
                                you and your team can connect accounts from the Accounts page.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Platform Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {platforms.map((platform) => (
                    <Card 
                        key={platform.platform} 
                        className="hover-lift"
                        data-testid={`oauth-card-${platform.platform}`}
                    >
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{platform.emoji}</span>
                                    <div>
                                        <CardTitle className="text-lg">{platform.name}</CardTitle>
                                        <CardDescription className="text-xs">
                                            {platform.configured ? (
                                                <span className="text-green-500 flex items-center gap-1">
                                                    <Check className="h-3 w-3" />
                                                    Configured
                                                </span>
                                            ) : (
                                                <span className="text-yellow-500 flex items-center gap-1">
                                                    <X className="h-3 w-3" />
                                                    Not configured
                                                </span>
                                            )}
                                        </CardDescription>
                                    </div>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pb-3">
                            {platform.configured && (
                                <div className="text-xs text-muted-foreground mb-3 font-mono bg-muted/50 p-2 rounded">
                                    ID: {platform.client_id_masked || '••••••••'}
                                </div>
                            )}
                            <a 
                                href={platform.setup_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                                <ExternalLink className="h-3 w-3" />
                                Developer Portal
                            </a>
                        </CardContent>
                        <CardFooter className="flex flex-col gap-2">
                            <div className="flex gap-2 w-full">
                                <Button 
                                    className="flex-1"
                                    variant={platform.configured ? "outline" : "default"}
                                    onClick={() => handleEdit(platform)}
                                    data-testid={`configure-${platform.platform}-btn`}
                                >
                                    <Key className="mr-2 h-4 w-4" />
                                    {platform.configured ? 'Update' : 'Configure'}
                                </Button>
                                {platform.configured && (
                                    <Button 
                                        variant="ghost" 
                                        size="icon"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => setDeleteDialog(platform)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                            {platform.configured && (
                                <Button 
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => handleTestConnection(platform.platform)}
                                    disabled={testing === platform.platform}
                                    data-testid={`test-${platform.platform}-btn`}
                                >
                                    {testing === platform.platform ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <PlayCircle className="mr-2 h-4 w-4" />
                                    )}
                                    Test Connection
                                </Button>
                            )}
                        </CardFooter>
                    </Card>
                ))}
            </div>

            {/* Test Result Dialog */}
            <Dialog open={!!testResult} onOpenChange={() => setTestResult(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {testResult?.success ? (
                                <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : testResult?.error_type === 'validation_warning' ? (
                                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            ) : (
                                <X className="h-5 w-5 text-red-500" />
                            )}
                            {testResult?.success ? 'Test Passed' : 'Test Result'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className={testResult?.success ? 'text-green-600' : testResult?.error_type === 'validation_warning' ? 'text-yellow-600' : 'text-red-600'}>
                            {testResult?.message}
                        </p>
                        
                        {testResult?.validation_errors && testResult.validation_errors.length > 0 && (
                            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                                <p className="font-medium text-yellow-600 mb-2">Warnings:</p>
                                <ul className="text-sm text-yellow-600 list-disc list-inside">
                                    {testResult.validation_errors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        
                        {testResult?.test_results && (
                            <div className="bg-muted/50 rounded-lg p-3">
                                <p className="font-medium text-sm mb-2">Details:</p>
                                <div className="text-xs text-muted-foreground space-y-1">
                                    <p>Client ID Length: {testResult.test_results.client_id_length} characters</p>
                                    <p>Client Secret Length: {testResult.test_results.client_secret_length} characters</p>
                                    {testResult.test_results.auth_url_constructable !== undefined && (
                                        <p>Auth URL: {testResult.test_results.auth_url_constructable ? '✓ Valid' : '✗ Invalid'}</p>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {testResult?.next_step && (
                            <p className="text-sm text-muted-foreground">
                                <strong>Next:</strong> {testResult.next_step}
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setTestResult(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!editPlatform} onOpenChange={() => setEditPlatform(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <span className="text-2xl">{editPlatform?.emoji}</span>
                            Configure {editPlatform?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Enter your OAuth credentials from the {editPlatform?.name} developer portal
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Callback URL */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">OAuth Callback URL</Label>
                            <div className="flex gap-2">
                                <Input 
                                    value={editPlatform?.callback_url || ''} 
                                    readOnly 
                                    className="font-mono text-xs bg-muted"
                                />
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={() => copyToClipboard(editPlatform?.callback_url)}
                                >
                                    <Copy className="h-4 w-4" />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Add this URL to your app's OAuth redirect settings
                            </p>
                        </div>

                        {/* Client ID */}
                        <div className="space-y-2">
                            <Label htmlFor="client_id">
                                {editPlatform?.client_id_label || 'Client ID'}
                            </Label>
                            <Input
                                id="client_id"
                                placeholder={editPlatform?.client_id_masked || 'Enter client ID'}
                                value={formData.client_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, client_id: e.target.value }))}
                                data-testid="oauth-client-id-input"
                            />
                        </div>

                        {/* Client Secret */}
                        <div className="space-y-2">
                            <Label htmlFor="client_secret">
                                {editPlatform?.client_secret_label || 'Client Secret'}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="client_secret"
                                    type={showSecret ? 'text' : 'password'}
                                    placeholder="Enter client secret"
                                    value={formData.client_secret}
                                    onChange={(e) => setFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                                    className="pr-10"
                                    data-testid="oauth-client-secret-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full"
                                    onClick={() => setShowSecret(!showSecret)}
                                >
                                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>

                        {/* Instructions */}
                        <Accordion type="single" collapsible>
                            <AccordionItem value="instructions">
                                <AccordionTrigger className="text-sm">
                                    Setup Instructions
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                                        {editPlatform?.instructions?.map((step, i) => (
                                            <li key={i}>{step}</li>
                                        ))}
                                    </ol>
                                    <a 
                                        href={editPlatform?.setup_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="mt-3 text-sm text-primary hover:underline flex items-center gap-1"
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        Open Developer Portal
                                    </a>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditPlatform(null)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Credentials
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Credentials</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the {deleteDialog?.name} OAuth credentials? 
                            Users won't be able to connect new {deleteDialog?.name} accounts until you reconfigure it.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={() => handleDelete(deleteDialog?.platform)}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
